import { useEffect, useRef, useState } from 'react'
import { AnchoredOverlay, Button, ampliarImagem, useToast } from '@orbind/ui'
import { RecortadorImagem } from '../../components/IconPicker.tsx'
import { garantirPermissao } from '../app/desktop-permissoes.ts'

/**
 * Foto do grupo no painel de dados, no gesto do mensageiro de referência:
 * quem modera clica na foto e escolhe Tirar foto / Carregar foto. As duas
 * passam pelo recortador redondo, onde a pessoa arrasta e dá zoom até o
 * enquadramento ficar certo — só o recorte sobe para o servidor.
 *
 * Quem não modera vê a foto e nada mais: o clique não abre menu.
 */
export function FotoDoGrupo({
  url,
  podeTrocar,
  onTrocar,
  onRemover,
}: {
  url: string | null
  podeTrocar: boolean
  onTrocar: (arquivo: File) => void
  onRemover: () => void
}) {
  const [menu, setMenu] = useState<HTMLElement | null>(null)
  const [camera, setCamera] = useState(false)
  const [recorte, setRecorte] = useState<File | null>(null)
  const inputArquivo = useRef<HTMLInputElement>(null)

  const imagem = url ? (
    <img className="ch-avatar__img" src={url} alt="Foto do grupo" />
  ) : (
    <i className="fas fa-users" aria-hidden />
  )

  return (
    <>
      {podeTrocar ? (
        <button
          type="button"
          className="ch-avatar ch-avatar--lg ch-info__fotobtn"
          aria-label="Alterar foto do grupo"
          aria-haspopup="menu"
          title="Alterar foto do grupo"
          onClick={(e) => setMenu(e.currentTarget)}
        >
          {imagem}
          <span className="ch-info__fotomascara" aria-hidden>
            <i className="fas fa-camera" />
          </span>
        </button>
      ) : (
        // `data-ob-ampliar`: quem não troca a foto pode ver ela grande no meio
        // da tela (pedido do usuário, 2026-09-16); clicar fora fecha.
        <span className="ch-avatar ch-avatar--lg" data-ob-ampliar>
          {imagem}
        </span>
      )}

      <input
        ref={inputArquivo}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const arquivo = e.target.files?.[0]
          if (arquivo) setRecorte(arquivo)
          e.target.value = ''
        }}
      />

      {menu ? (
        <AnchoredOverlay anchor={menu} onClose={() => setMenu(null)} prefer="direita" role="menu" aria-label="Foto do grupo" className="ch-menu">
          {url ? (
            <button
              type="button"
              role="menuitem"
              className="ch-more__item"
              onClick={() => {
                setMenu(null)
                ampliarImagem(url)
              }}
            >
              <i className="fas fa-search-plus" aria-hidden /> Ver foto
            </button>
          ) : null}
          <button
            type="button"
            role="menuitem"
            className="ch-more__item"
            onClick={() => {
              setMenu(null)
              setCamera(true)
            }}
          >
            <i className="fas fa-camera" aria-hidden /> Tirar foto
          </button>
          <button
            type="button"
            role="menuitem"
            className="ch-more__item"
            onClick={() => {
              setMenu(null)
              inputArquivo.current?.click()
            }}
          >
            <i className="far fa-folder-open" aria-hidden /> Carregar foto
          </button>
          {url ? (
            <button
              type="button"
              role="menuitem"
              className="ch-more__item"
              onClick={() => {
                setMenu(null)
                onRemover()
              }}
            >
              <i className="fas fa-trash-alt" aria-hidden /> Remover foto
            </button>
          ) : null}
        </AnchoredOverlay>
      ) : null}

      {camera ? (
        <CapturaDaCamera
          onCancelar={() => setCamera(false)}
          onCapturar={(arquivo) => {
            setCamera(false)
            setRecorte(arquivo)
          }}
        />
      ) : null}

      {recorte ? (
        <RecortadorImagem
          arquivo={recorte}
          titulo="Ajustar foto do grupo"
          rotuloConfirmar="Usar foto"
          redondo
          saida={512}
          onCancelar={() => setRecorte(null)}
          onConfirmar={(arquivo) => {
            setRecorte(null)
            onTrocar(arquivo)
          }}
        />
      ) : null}
    </>
  )
}

/**
 * Câmera do computador (ou do celular) numa caixa, com o botão de disparar.
 *
 * No desktop o `capture` do input de arquivo é ignorado — só abriria o
 * seletor de arquivos —, por isso a câmera é aberta direto. A câmera é
 * desligada ao fechar: deixar o stream vivo manteria a luz do aparelho acesa.
 */
function CapturaDaCamera({ onCancelar, onCapturar }: { onCancelar: () => void; onCapturar: (arquivo: File) => void }) {
  const video = useRef<HTMLVideoElement>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [pronta, setPronta] = useState(false)
  const toast = useToast()

  useEffect(() => {
    let stream: MediaStream | null = null
    let vivo = true
    if (!navigator.mediaDevices?.getUserMedia) {
      setErro('Este aparelho não oferece acesso à câmera.')
      return
    }
    // no desktop quem pergunta é o Orbind (SPEC-20260916-2335)
    garantirPermissao('camera')
      .then((ok) => {
        if (!ok) throw new Error('sem permissão')
        return navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false })
      })
      .then((s) => {
        if (!vivo) {
          s.getTracks().forEach((t) => t.stop())
          return
        }
        stream = s
        if (video.current) {
          video.current.srcObject = s
          void video.current.play()
        }
      })
      .catch((e: unknown) => {
        const nome = e instanceof DOMException ? e.name : ''
        const semPermissao = nome === 'NotAllowedError' || (e instanceof Error && e.message === 'sem permissão')
        setErro(
          semPermissao
            ? 'O acesso à câmera foi negado. Libere a permissão e tente de novo.'
            : 'Nenhuma câmera disponível.',
        )
      })
    return () => {
      vivo = false
      stream?.getTracks().forEach((t) => t.stop())
    }
  }, [])

  const disparar = () => {
    const v = video.current
    if (!v || v.videoWidth === 0) return
    const canvas = document.createElement('canvas')
    canvas.width = v.videoWidth
    canvas.height = v.videoHeight
    canvas.getContext('2d')?.drawImage(v, 0, 0)
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          toast.push({ tone: 'danger', message: 'Não foi possível capturar a foto.' })
          return
        }
        onCapturar(new File([blob], 'foto-do-grupo.jpg', { type: 'image/jpeg' }))
      },
      'image/jpeg',
      0.92,
    )
  }

  return (
    <div className="icp-crop">
      <div className="icp-crop__scrim" onClick={onCancelar} aria-hidden />
      <div className="icp-crop__caixa" role="dialog" aria-label="Tirar foto">
        <h3 className="icp-crop__titulo">Tirar foto</h3>
        {erro ? (
          <p className="ch-contact__sub ch-camera__erro">{erro}</p>
        ) : (
          <video
            ref={video}
            className="ch-camera__video"
            muted
            playsInline
            onLoadedData={() => setPronta(true)}
          />
        )}
        <div className="icp-crop__acoes">
          <Button variant="ghost" onClick={onCancelar}>
            Cancelar
          </Button>
          <Button variant="primary" icon="fas fa-camera" disabled={!pronta || erro !== null} onClick={disparar}>
            Tirar foto
          </Button>
        </div>
      </div>
    </div>
  )
}
